<template>
  <section v-if="showSection" class="featured-products-section-wrapper py-3">
    <div class="container-fluid">
      <!-- Loading State -->
      <div v-if="loading" class="row">
        <div class="col-12">
          <div class="featured-products-section" data-aos="fade-up">
            <h3 class="featured-title text-center mb-4">Προτεινόμενα Προϊόντα</h3>
            <div class="products-loading">
              <div class="loading-spinner"></div>
              <p class="loading-text">Φόρτωση προϊόντων...</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Featured Products -->
      <div v-else-if="products.length > 0" class="row">
        <div class="col-12">
          <div class="featured-products-section">
            <h3 class="featured-title text-center mb-4" data-aos="fade-up">
              Προτεινόμενα Προϊόντα
            </h3>
            <div class="row g-4">
              <div 
                v-for="(product, index) in products.slice(0, 4)" 
                :key="product.id"
                class="col-xl-3 col-lg-6 col-md-6 col-6"
                data-aos="fade-up"
                :data-aos-delay="index * 100"
              >
                <div class="product-card" @click="$emit('viewProduct', product)">
                  <!-- Product Image -->
                  <div class="product-image">
                    <img 
                      :src="getProductImageUrl(product.main_image)"
                      :alt="product.name"
                      class="product-img"
                      @error="handleImageError"
                    >
                    
                    <!-- Badges -->
                    <div class="product-badges">
                      <span v-if="product.is_featured" class="badge featured-badge">
                        <i class="bi bi-star-fill"></i> Προτεινόμενο
                      </span>
                      <span v-if="product.original_price && product.original_price > product.price" class="badge sale-badge">
                        -{{ Math.round(((product.original_price - product.price) / product.original_price) * 100) }}%
                      </span>
                    </div>
                    
                    <!-- Always Visible Actions -->
                    <div class="product-actions-overlay">
                      <button 
                        class="action-btn view-btn" 
                        @click.stop="$emit('viewProduct', product)" 
                        title="Προβολή"
                      >
                        <i class="bi bi-eye"></i>
                      </button>
                      <button 
                        class="action-btn wishlist-btn" 
                        :class="{ 'active-wishlist': isInWishlist(product.id) }"
                        @click.stop="$emit('toggleWishlist', product)"
                        title="Wishlist"
                      >
                        <i class="bi" :class="isInWishlist(product.id) ? 'bi-heart-fill' : 'bi-heart'"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Product Info -->
                  <div class="product-info">
                    <div class="product-brand">{{ product.brand_name || 'Χωρίς μάρκα' }}</div>
                    
                    <div class="product-name-row">
                      <i class="category-icon" :class="getCategoryIcon(product.category_name)"></i>
                      <h5 class="product-name" :title="product.name">{{ product.name }}</h5>
                    </div>
                    
                    <div class="product-category">
                      <i class="bi" :class="getCategoryIcon(product.category_name)"></i>
                      {{ product.category_name || 'Χωρίς κατηγορία' }}
                    </div>
                    
                    <p v-if="product.description" class="product-description">
                      {{ product.description.length > 80 ? product.description.substring(0, 80) + '...' : product.description }}
                    </p>
                    
                    <div class="product-pricing">
                      <span v-if="product.original_price && product.original_price > product.price" class="sale-price">€{{ formatPrice(product.price) }}</span>
                      <span v-if="product.original_price && product.original_price > product.price" class="original-price">€{{ formatPrice(product.original_price) }}</span>
                      <span v-else class="current-price">€{{ formatPrice(product.price) }}</span>
                    </div>
                  </div>

                  <!-- Card Actions -->
                  <div class="product-card-actions">
                    <button 
                      class="btn btn-primary add-to-cart-btn w-100"
                      @click.stop="$emit('addToCart', product)"
                    >
                      <i class="bi bi-cart-plus me-2"></i>
                      Αγορά
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- View All Products Button -->
            <div class="text-center mt-4">
              <router-link to="/shop" class="btn btn-outline-primary btn-lg view-all-btn">
                <i class="bi bi-arrow-right me-2"></i>
                Δείτε όλα τα προϊόντα
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else class="row">
        <div class="col-12 text-center">
          <div class="empty-products-state">
            <div class="empty-icon">
              <i class="bi bi-box-seam"></i>
            </div>
            <h4 class="empty-title">Δεν υπάρχουν διαθέσιμα προϊόντα</h4>
            <p class="empty-description">Τα προϊόντα μας θα είναι σύντομα διαθέσιμα.</p>
            <router-link to="/contact" class="btn btn-primary">
              Επικοινωνήστε μαζί μας
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { IMAGE_BASE_URL, API_BASE_URL } from '@/config/env'

interface Product {
  id: number
  name: string
  description?: string
  main_image: string
  price: number
  original_price?: number
  is_featured: boolean
  brand_name?: string
  category_name?: string
  // Additional optional properties for compatibility
  slug?: string
  rating?: number
  stock?: number
  [key: string]: any
}

interface Props {
  showSection: boolean
  products: Product[]
  loading?: boolean
  wishlistIds?: number[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  wishlistIds: () => []
})

defineEmits<{
  viewProduct: [product: any]
  toggleWishlist: [product: any]
  addToCart: [product: any]
}>()

const isInWishlist = (productId: number) => {
  return props.wishlistIds.includes(productId)
}

const getProductImageUrl = (image: string) => {
  return `${IMAGE_BASE_URL}?type=product&file=${image}`
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.src = `${API_BASE_URL}/placeholder.php?width=300&height=300&text=No+Image`
}

const formatPrice = (price: number) => {
  return price.toFixed(2)
}

const getCategoryIcon = (categoryName?: string) => {
  if (!categoryName) return 'bi-tag'
  const icons: Record<string, string> = {
    'Γυαλιά Ηλίου': 'bi-brightness-high',
    'Γυαλιά Οράσεως': 'bi-eyeglasses',
    'Φακοί Επαφής': 'bi-circle',
    'Αξεσουάρ': 'bi-bag'
  }
  return icons[categoryName] || 'bi-tag'
}
</script>

<style scoped>
.featured-products-section-wrapper {
  background: white;
}

.featured-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.products-loading {
  text-align: center;
  padding: 3rem 0;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0d6efd;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.product-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

.product-image {
  position: relative;
  height: 250px;
  overflow: hidden;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-img {
  transform: scale(1.1);
}

.product-badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 2;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.featured-badge {
  background: #ffc107;
  color: #000;
}

.sale-badge {
  background: #dc3545;
  color: white;
}

.product-actions-overlay {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 2;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.view-btn {
  background: rgba(13, 110, 253, 0.9);
  color: white;
}

.view-btn:hover {
  background: #0d6efd;
  transform: scale(1.1);
}

.wishlist-btn {
  background: rgba(220, 53, 69, 0.9);
  color: white;
}

.wishlist-btn:hover,
.wishlist-btn.active-wishlist {
  background: #dc3545;
  transform: scale(1.1);
}

.product-info {
  padding: 1rem;
  flex: 1;
}

.product-brand {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.product-name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.product-name {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-category {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.product-description {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.product-pricing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.sale-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #dc3545;
}

.original-price {
  font-size: 0.95rem;
  color: #6c757d;
  text-decoration: line-through;
}

.current-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #28a745;
}

.product-card-actions {
  padding: 0 1rem 1rem;
}

.add-to-cart-btn {
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.add-to-cart-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
}

.empty-products-state {
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  color: #dee2e6;
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.empty-description {
  color: #adb5bd;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .featured-title {
    font-size: 1.75rem;
  }
  
  .product-image {
    height: 200px;
  }
  
  .product-name {
    font-size: 0.9rem;
  }
}
</style>
